import moment from 'moment'
import {
  buildComparisonFooter,
  formatCurrency,
  formatPercent,
  type ComparisonFooter,
} from '@utils/dashboardFinance.util'
import { hasNonZeroDashboardValues } from '@utils/dashboardSectionEmpty.util'
import type { DashboardMetricItemData } from '@utils/dashboardMetrics.util'
import type {
  HuddleGoalAttainmentRow,
  HuddleGoalMeasureUnit,
  HuddleEventPerson,
  HuddleEventsResult,
  HuddleLeaderboardProviderRow,
  HuddleLeaderboardSummary,
  HuddlePeriodComparisonMetric,
  HuddleProviderBreakdownRow,
  HuddleProviderPerformanceRow,
  HuddleProviderPerformanceSummary,
  HuddleScheduleEventRow,
  HuddleTodayForecastAppointmentRow,
  HuddleTodayForecastSummary,
  HuddleTomorrowForecastAppointmentRow,
  HuddleTomorrowForecastSummary,
  HuddleTomorrowGoalsSummary,
  HuddleTomorrowScheduleAtAGlanceRow,
  HuddleTomorrowScheduleAtAGlanceSummary,
  HuddleYesterdayActualsBreakdownRow,
  HuddleYesterdayActualsSummary,
  HuddleYesterdayPerformanceSummary,
  HuddleYesterdayProviderActualsSummary,
  HuddleYesterdayScheduleAdjustmentsSummary,
  HuddleYesterdaysPerformanceProviderRow,
  HuddleYesterdaysPerformanceSummary,
  HuddleTodayForecastResult,
  HuddleTomorrowForecastResult,
  HuddleProviderPerformanceResult,
  HuddleYesterdaysPerformanceResult,
  HuddleYesterdayActualsResult,
  HuddleYesterdayScheduleAdjustmentsResult,
  HuddleYesterdayPerformanceResult,
  HuddleTomorrowScheduleAtAGlanceResult,
  HuddleTomorrowGoalsResult,
  HuddleLeaderboardResult,
  HuddleYesterdayProviderActualsResult,
} from '@services/api/dashboard/huddle.api'

// --- Shared helpers ---

const MINUS = '−'

const ORDINAL_SUFFIXES: Record<number, string> = {
  1: 'st',
  2: 'nd',
  3: 'rd',
}

function formatRank(index: number): string {
  const n = index + 1
  const mod100 = n % 100
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? 'th' : (ORDINAL_SUFFIXES[n % 10] ?? 'th')
  return `${n}${suffix}`
}

function formatGoalVariance(variance: number | null | undefined): string {
  if (variance == null) return '—'
  if (variance === 0) return formatCurrency(0, { compact: true })

  const formatted = formatCurrency(Math.abs(variance), { compact: true })
  return variance > 0 ? `+${formatted}` : `${MINUS}${formatted}`
}

function buildCurrencyComparisonTile(
  id: string,
  leftLabel: string,
  metric: HuddlePeriodComparisonMetric,
  options?: { rightLabel?: string },
): DashboardMetricItemData {
  const footer = buildComparisonFooter(metric, { isCurrency: true })
  return {
    id,
    leftLabel,
    rightLabel: options?.rightLabel,
    value: formatCurrency(metric.current, { compact: true }),
    footer: footer.text,
    footerTrend: footer.up ? 'up' : 'down',
  }
}

function buildFlexibleComparisonTile(
  id: string,
  leftLabel: string,
  metric: HuddlePeriodComparisonMetric,
  options: { isCurrency?: boolean; isCount?: boolean },
): DashboardMetricItemData {
  const footer = buildComparisonFooter(metric, options)
  return {
    id,
    leftLabel,
    value: options.isCurrency
      ? formatCurrency(metric.current, { compact: true })
      : String(Math.round(metric.current)),
    footer: footer.text,
    footerTrend: footer.up ? 'up' : 'down',
  }
}

function buildPercentFooter(
  metric: HuddlePeriodComparisonMetric,
): ComparisonFooter {
  const up = metric.percentChange >= 0
  const pct = Math.abs(metric.percentChange)
  const sign = pct > 0 ? `${pct.toFixed(1)}%` : '0%'
  return {
    text: `${sign} (${formatPercent(metric.previous)}) vs prior period`,
    up,
  }
}

function buildPercentComparisonTile(
  id: string,
  leftLabel: string,
  metric: HuddlePeriodComparisonMetric,
): DashboardMetricItemData {
  const footer = buildPercentFooter(metric)
  return {
    id,
    leftLabel,
    value: formatPercent(metric.current),
    footer: footer.text,
    footerTrend: footer.up ? 'up' : 'down',
  }
}

type HuddleForecastSummary = {
  expectedProduction: HuddlePeriodComparisonMetric
  scheduledProduction: HuddlePeriodComparisonMetric
  pendingTreatments: HuddlePeriodComparisonMetric
  arDue: HuddlePeriodComparisonMetric
  arOver90: number
}

function mapHuddleForecastSummaryToMetrics(
  summary: HuddleForecastSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  return [
    buildCurrencyComparisonTile(
      'expected-production',
      'Expected Production',
      summary.expectedProduction,
    ),
    buildCurrencyComparisonTile(
      'scheduled-production',
      'Scheduled production',
      summary.scheduledProduction,
    ),
    buildCurrencyComparisonTile(
      'pending-treatments',
      'Pending treatments',
      summary.pendingTreatments,
    ),
    {
      id: 'ar-due',
      leftLabel: 'AR Due',
      value: formatCurrency(summary.arDue.current, { compact: true }),
      footer: `${formatCurrency(summary.arOver90, { compact: true })} over 90 days`,
    },
  ]
}

function formatTomorrowGoalTargetLabel(
  row: HuddleGoalAttainmentRow,
): string | undefined {
  if (row.goal == null) return 'Target —'
  if (row.unit === 'count') return `Target ${row.goal}`
  return `Target ${formatCurrency(row.goal, { compact: true })}`
}

function formatTomorrowGoalProjectedValue(
  row: HuddleGoalAttainmentRow,
): string {
  if (row.unit === 'count') return String(row.actual)
  return formatCurrency(row.actual, { compact: true })
}

function mapTomorrowGoalRowToMetric(
  id: string,
  leftLabel: string,
  row: HuddleGoalAttainmentRow,
): DashboardMetricItemData {
  return {
    id,
    leftLabel,
    value: formatTomorrowGoalProjectedValue(row),
    rightLabel: formatTomorrowGoalTargetLabel(row),
  }
}

function formatMeasureValue(
  value: number,
  unit: HuddleGoalMeasureUnit,
): string {
  if (unit === 'currency') {
    return formatCurrency(value, { compact: true })
  }
  if (unit === 'percent') {
    return `${Math.round(value)}%`
  }
  return String(Math.round(value))
}

function formatPerformanceVariance(row: HuddleGoalAttainmentRow): string {
  const absVariance = Math.abs(row.variance)
  if (row.variance === 0) return '0'

  if (row.unit === 'currency') {
    const formatted = formatCurrency(absVariance, { compact: true })
    return row.variance < 0 ? `${MINUS}${formatted}` : `+${formatted}`
  }

  if (row.unit === 'percent') {
    const points = Math.round(absVariance)
    return row.variance < 0 ? `${MINUS}${points} pts` : `+${points} pts`
  }

  const count = Math.round(absVariance)
  return row.variance < 0 ? `${MINUS}${count}` : `+${count}`
}

function formatGoalAttainmentFooter(row: HuddleGoalAttainmentRow): string {
  if (row.percentOfGoal == null) return 'No goal set'

  const direction =
    row.variance < 0 ? 'Below' : row.variance > 0 ? 'Above' : 'At'
  const absVariance = Math.abs(row.variance)

  if (row.unit === 'currency') {
    return `${direction} target by ${formatCurrency(absVariance, { compact: true })}`
  }

  if (row.unit === 'percent') {
    return `${direction} target by ${Math.round(absVariance)} pts`
  }

  return `${direction} target by ${Math.round(absVariance)}`
}

function mapGoalRowToSummaryMetric(
  id: string,
  leftLabel: string,
  row: HuddleGoalAttainmentRow,
): DashboardMetricItemData {
  return {
    id,
    leftLabel,
    value:
      row.percentOfGoal != null ? `${Math.round(row.percentOfGoal)}%` : '—',
    valueOver: row.percentOfGoal != null ? 'of goal' : undefined,
    footer: formatGoalAttainmentFooter(row),
  }
}

// --- Leaderboard ---

export function mapHuddleLeaderboardSummaryToMetrics(
  summary: HuddleLeaderboardSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  const tiles: DashboardMetricItemData[] = [
    buildCurrencyComparisonTile(
      'lb-total-production',
      'Total production collected',
      summary.totalProduction,
    ),
  ]

  if (summary.topPerformer) {
    tiles.push({
      id: 'lb-top-performer',
      leftLabel: 'Top performer',
      value: summary.topPerformer.name,
      footer: `${formatCurrency(summary.topPerformer.production, { compact: true })} collected today`,
    })
  } else {
    tiles.push({
      id: 'lb-top-performer',
      leftLabel: 'Top performer',
      value: '—',
      footer: 'No production',
    })
  }

  tiles.push(
    buildCurrencyComparisonTile(
      'lb-practice-average',
      'Practice average',
      summary.practiceAverage,
      { rightLabel: 'per provider' },
    ),
    {
      id: 'lb-overall-goal',
      leftLabel: 'Overall goal attainment',
      rightLabel: 'Goal 100%',
      value:
        summary.overallGoalAttainmentPct != null
          ? formatPercent(summary.overallGoalAttainmentPct, 0)
          : '—',
      footer:
        summary.providersWithGoals > 0
          ? `${summary.providersAtOrAboveGoal} of ${summary.providersWithGoals} providers at or above goal`
          : 'No provider goals set',
    },
  )

  return tiles
}

export type HuddleLeaderboardCardRow = {
  id: string
  rank: string
  name: string
  role: string
  productionCollected: string
}

export type HuddleLeaderboardDetailTableRow = {
  id: string
  rank: string
  provider: string
  role: string
  productionCollected: string
  collections: string
  appointmentsCompleted: string
  goalAttainment: string
  productionGoal: string
}

export function mapHuddleLeaderboardRowsToCard(
  providers: HuddleLeaderboardProviderRow[] | null | undefined,
): HuddleLeaderboardCardRow[] {
  if (!providers?.length) return []

  return providers.map((row, index) => ({
    id: row.providerId,
    rank: formatRank(index),
    name: row.provider,
    role: row.role,
    productionCollected: formatCurrency(row.production, { compact: true }),
  }))
}

export function mapHuddleLeaderboardRowsToDetailTable(
  providers: HuddleLeaderboardProviderRow[] | null | undefined,
): HuddleLeaderboardDetailTableRow[] {
  if (!providers?.length) return []

  return providers.map((row, index) => ({
    id: row.providerId,
    rank: formatRank(index),
    provider: row.provider,
    role: row.role,
    productionCollected: formatCurrency(row.production, { compact: true }),
    collections: formatCurrency(row.collections, { compact: true }),
    appointmentsCompleted: String(Math.round(row.appointmentsCompleted)),
    goalAttainment:
      row.goalAttainmentPct != null
        ? formatPercent(row.goalAttainmentPct, 0)
        : '—',
    productionGoal: formatGoalVariance(row.goalVariance),
  }))
}

// --- Provider performance ---

export function formatAcceptedTreatmentDuration(totalMinutes: number): string {
  if (totalMinutes <= 0) return '—'

  const hours = Math.floor(totalMinutes / 60)
  const minutes = totalMinutes % 60

  if (hours > 0 && minutes > 0) {
    const hourLabel = hours === 1 ? 'hr' : 'hrs'
    return `${hours} ${hourLabel} ${minutes} mins`
  }

  if (hours > 0) {
    return `${hours} ${hours === 1 ? 'hr' : 'hrs'}`
  }

  return `${minutes} ${minutes === 1 ? 'min' : 'mins'}`
}

export function mapHuddleProviderPerformanceSummaryToMetrics(
  summary: HuddleProviderPerformanceSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  const hourlyFooter = buildComparisonFooter(summary.hourlyProduction, {
    isCurrency: true,
  })

  return [
    buildCurrencyComparisonTile(
      'same-day-tx',
      'Same day treatment',
      summary.sameDayTreatment,
    ),
    buildCurrencyComparisonTile(
      'accepted-tx',
      'Accepted treatment',
      summary.acceptedTreatment,
    ),
    {
      id: 'hourly-production',
      leftLabel: 'Hourly production',
      value: formatCurrency(summary.hourlyProduction.current, {
        compact: true,
      }),
      footer: hourlyFooter.text,
      footerTrend: hourlyFooter.up ? 'up' : 'down',
    },
    buildPercentComparisonTile('perio-rate', 'Perio rate', summary.perioRate),
    buildPercentComparisonTile(
      'hygiene-utilization',
      'Hygiene utilization',
      summary.hygieneUtilization,
    ),
    buildCurrencyComparisonTile(
      'collections',
      'Collections',
      summary.collections,
    ),
  ]
}

export function mapHuddleProviderPerformanceSummaryToGridMetrics(
  summary: HuddleProviderPerformanceSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  const sameDayFooter = buildComparisonFooter(summary.sameDayTreatment, {
    isCurrency: true,
  })
  const acceptedFooter = buildComparisonFooter(summary.acceptedTreatment, {
    isCurrency: true,
  })

  const perioFooterGrid = buildPercentFooter(summary.perioRate)
  const hygieneFooterGrid = buildPercentFooter(summary.hygieneUtilization)

  return [
    {
      id: 'same-day-tx',
      leftLabel: 'Same day treatment',
      value: formatCurrency(summary.sameDayTreatment.current, {
        compact: true,
      }),
      footer: sameDayFooter.text,
      footerTrend: sameDayFooter.up ? 'up' : 'down',
    },
    {
      id: 'accepted-tx',
      leftLabel: 'Accepted treatment',
      value: formatCurrency(summary.acceptedTreatment.current, {
        compact: true,
      }),
      footer: acceptedFooter.text,
      footerTrend: acceptedFooter.up ? 'up' : 'down',
    },
    {
      id: 'perio-rate',
      leftLabel: 'Perio rate',
      value: formatPercent(summary.perioRate.current),
      footer: perioFooterGrid.text,
      footerTrend: perioFooterGrid.up ? 'up' : 'down',
    },
    {
      id: 'hygiene-util',
      leftLabel: 'Hygiene Utilization %',
      value: formatPercent(summary.hygieneUtilization.current),
      footer: hygieneFooterGrid.text,
      footerTrend: hygieneFooterGrid.up ? 'up' : 'down',
    },
  ]
}

export type HuddleProviderPerformanceTableRow = {
  id: string
  provider: string
  role: string
  sameDayTreatment: string
  acceptedTreatment: string
  hourlyProduction: string
  perioRate: string
  collections: string
}

export function mapHuddleProviderPerformanceRowsToTable(
  providers: HuddleProviderPerformanceRow[] | null | undefined,
): HuddleProviderPerformanceTableRow[] {
  if (!providers?.length) return []

  return providers.map((row) => ({
    id: row.providerId,
    provider: row.provider,
    role: row.role,
    sameDayTreatment:
      row.sameDayTreatmentPct == null
        ? '—'
        : formatPercent(row.sameDayTreatmentPct),
    acceptedTreatment: formatAcceptedTreatmentDuration(
      row.acceptedTreatmentMinutes,
    ),
    hourlyProduction: formatCurrency(row.hourlyProduction, { compact: true }),
    perioRate: row.perioRate == null ? '—' : formatPercent(row.perioRate),
    collections: formatCurrency(row.collections, { compact: true }),
  }))
}

// --- Today forecast ---

export function mapHuddleTodayForecastSummaryToMetrics(
  summary: HuddleTodayForecastSummary | null | undefined,
): DashboardMetricItemData[] {
  return mapHuddleForecastSummaryToMetrics(summary)
}

export type HuddleTodayForecastTableRow = {
  id: string
  dateTime: string
  patientId: string
  patientName: string
  provider: string
  appointmentType: string
  operatory: string
  amount: string
}

export function mapHuddleTodayForecastAppointmentsToTableRows(
  appointments: HuddleTodayForecastAppointmentRow[] | null | undefined,
): HuddleTodayForecastTableRow[] {
  if (!appointments?.length) return []

  return appointments.map((row) => ({
    id: row.appointmentId,
    dateTime: row.dateTime ? moment(row.dateTime).format('h:mm A') : '—',
    patientId: row.patientId,
    patientName: row.patientName,
    provider: row.provider,
    appointmentType: row.appointmentType,
    operatory: row.operatory,
    amount: formatCurrency(row.amount, { compact: true }),
  }))
}

// --- Tomorrow forecast ---

export function mapHuddleTomorrowForecastSummaryToMetrics(
  summary: HuddleTomorrowForecastSummary | null | undefined,
): DashboardMetricItemData[] {
  return mapHuddleForecastSummaryToMetrics(summary)
}

export type HuddleTomorrowForecastTableRow = {
  id: string
  time: string
  patientId: string
  patientName: string
  provider: string
  appointmentType: string
  operatory: string
  amount: string
  confirmation: string
}

export function mapHuddleTomorrowForecastAppointmentsToTableRows(
  appointments: HuddleTomorrowForecastAppointmentRow[] | null | undefined,
): HuddleTomorrowForecastTableRow[] {
  if (!appointments?.length) return []

  return appointments.map((row) => ({
    id: row.appointmentId,
    time: row.dateTime ? moment(row.dateTime).format('h:mm A') : '—',
    patientId: row.patientId,
    patientName: row.patientName,
    provider: row.provider,
    appointmentType: row.appointmentType,
    operatory: row.operatory,
    amount: formatCurrency(row.amount, { compact: true }),
    confirmation: row.confirmationStatus,
  }))
}

// --- Tomorrow goals ---

export function mapHuddleTomorrowGoalsToMetrics(
  goals: HuddleTomorrowGoalsSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!goals) return []

  return [
    mapTomorrowGoalRowToMetric(
      'tm-goal-production',
      'Production goal',
      goals.production,
    ),
    mapTomorrowGoalRowToMetric(
      'tm-goal-collections',
      'Collections goal',
      goals.collections,
    ),
    mapTomorrowGoalRowToMetric(
      'tm-goal-patients',
      'Patient goal',
      goals.patients,
    ),
  ]
}

// --- Yesterday actuals ---

const PATIENTS_SEEN_CATEGORY = 'Patients seen'

export function mapHuddleSummaryToMetrics(
  summary: HuddleYesterdayActualsSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  return [
    buildFlexibleComparisonTile(
      'y-actual-production',
      'Production (actual)',
      summary.production,
      { isCurrency: true },
    ),
    buildFlexibleComparisonTile(
      'y-actual-collections',
      'Collections (actual)',
      summary.collections,
      { isCurrency: true },
    ),
    buildFlexibleComparisonTile(
      'y-patients-seen',
      'Patients seen',
      summary.patientsSeen,
      { isCount: true },
    ),
    buildFlexibleComparisonTile(
      'y-new-patients',
      'New patients',
      summary.newPatients,
      { isCount: true },
    ),
  ]
}

export type HuddleYesterdayActualsTableRow = {
  id: string
  category: string
  amount: string
}

export function mapHuddleBreakdownToTableRows(
  rows: HuddleYesterdayActualsBreakdownRow[] | null | undefined,
): HuddleYesterdayActualsTableRow[] {
  if (!rows?.length) return []

  return rows.map((row) => ({
    id: row.category,
    category: row.category,
    amount:
      row.category === PATIENTS_SEEN_CATEGORY
        ? String(Math.round(row.amount))
        : formatCurrency(row.amount, { compact: true }),
  }))
}

// --- Yesterday performance vs goal ---

export function mapHuddlePerformanceSummaryToMetrics(
  summary: HuddleYesterdayPerformanceSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  return [
    mapGoalRowToSummaryMetric(
      'y-perf-production',
      'Production',
      summary.production,
    ),
    mapGoalRowToSummaryMetric(
      'y-perf-collections',
      'Collections',
      summary.collections,
    ),
  ]
}

export type HuddlePerformanceTableRow = {
  id: string
  measure: string
  goal: string
  actual: string
  pctOfGoal: string
  variance: string
}

export function mapHuddlePerformanceMeasuresToTableRows(
  measures: HuddleGoalAttainmentRow[] | null | undefined,
): HuddlePerformanceTableRow[] {
  if (!measures?.length) return []

  return measures.map((row) => ({
    id: row.id,
    measure: row.measure,
    goal: row.goal != null ? formatMeasureValue(row.goal, row.unit) : '—',
    actual: formatMeasureValue(row.actual, row.unit),
    pctOfGoal:
      row.percentOfGoal != null ? `${Math.round(row.percentOfGoal)}%` : '—',
    variance: formatPerformanceVariance(row),
  }))
}

// --- Yesterday provider actuals ---

export type HuddleProviderDetailTableRow = {
  id: string
  provider: string
  role: string
  production: string
  collections: string
  patientsSeen: string
}

export type HuddleProviderLeaderboardRow = {
  id: string
  rank: string
  name: string
  role: string
  productionCollected: string
}

export function mapHuddleProviderSummaryToMetrics(
  summary: HuddleYesterdayProviderActualsSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  const tiles: DashboardMetricItemData[] = [
    buildFlexibleComparisonTile(
      'y-prov-total-production',
      'Total production',
      summary.totalProduction,
      { isCurrency: true },
    ),
    buildFlexibleComparisonTile(
      'y-prov-total-collections',
      'Total collections',
      summary.totalCollection,
      { isCurrency: true },
    ),
  ]

  if (summary.topPerformer) {
    tiles.push({
      id: 'y-prov-top-performer',
      leftLabel: 'Top performer',
      value: summary.topPerformer.name,
      footer: `${formatCurrency(summary.topPerformer.production, { compact: true })} production`,
    })
  } else {
    tiles.push({
      id: 'y-prov-top-performer',
      leftLabel: 'Top performer',
      value: '—',
      footer: 'No production',
    })
  }

  tiles.push(
    buildFlexibleComparisonTile(
      'y-prov-patients-seen',
      'Patients seen',
      summary.patientsSeen,
      { isCount: true },
    ),
  )

  return tiles
}

export function mapHuddleProviderBreakdownToTableRows(
  rows: HuddleProviderBreakdownRow[] | null | undefined,
): HuddleProviderDetailTableRow[] {
  if (!rows?.length) return []

  return rows.map((row) => ({
    id: row.providerId,
    provider: row.provider,
    role: row.role,
    production: formatCurrency(row.production, { compact: true }),
    collections: formatCurrency(row.collection, { compact: true }),
    patientsSeen: String(Math.round(row.patientsSeen)),
  }))
}

export function mapHuddleProviderBreakdownToLeaderboardRows(
  rows: HuddleProviderBreakdownRow[] | null | undefined,
): HuddleProviderLeaderboardRow[] {
  if (!rows?.length) return []

  return rows.map((row, index) => ({
    id: row.providerId,
    rank: formatRank(index),
    name: row.provider,
    role: row.role,
    productionCollected: formatCurrency(row.production, { compact: true }),
  }))
}

// --- Yesterday's performance (today tab card) ---

export function mapHuddleYesterdaysPerformanceSummaryToMetrics(
  summary: HuddleYesterdaysPerformanceSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  const productionGoalLabel =
    summary.productionGoal != null
      ? `Goal ${formatCurrency(summary.productionGoal, { compact: true })}`
      : undefined

  return [
    buildCurrencyComparisonTile(
      'yp-production',
      'Production',
      summary.production,
      {
        rightLabel: productionGoalLabel,
      },
    ),
    buildCurrencyComparisonTile(
      'yp-collections',
      'Collections',
      summary.collections,
    ),
    buildCurrencyComparisonTile(
      'yp-scheduled-appt',
      'Scheduled appointments',
      summary.scheduledAppointments,
    ),
  ]
}

export type HuddleYesterdaysPerformanceTableRow = {
  id: string
  provider: string
  role: string
  production: string
  collections: string
  scheduledAppointments: string
  productionGoal: string
  goalAttainment: string
}

export function mapHuddleYesterdaysPerformanceRowsToTable(
  providers: HuddleYesterdaysPerformanceProviderRow[] | null | undefined,
): HuddleYesterdaysPerformanceTableRow[] {
  if (!providers?.length) return []

  return providers.map((row) => ({
    id: row.providerId,
    provider: row.provider,
    role: row.role,
    production: formatCurrency(row.production, { compact: true }),
    collections: formatCurrency(row.collections, { compact: true }),
    scheduledAppointments: formatCurrency(row.scheduledAppointments, {
      compact: true,
    }),
    productionGoal:
      row.productionGoal != null
        ? formatCurrency(row.productionGoal, { compact: true })
        : '—',
    goalAttainment:
      row.goalAttainmentPct != null
        ? formatPercent(row.goalAttainmentPct)
        : '—',
  }))
}

export type HuddleEventsColumn = {
  id: string
  leftLabel: string
  people: HuddleEventPerson[]
}

const HUDDLE_EVENTS_DATE_FORMAT = 'MMMM D, YYYY'

/** Calendar day as YYYY-MM-DD for huddle events API (avoids UTC shift from toISOString). */
export function toHuddleEventsQueryDate(date: string): string {
  const parsed = moment(date, HUDDLE_EVENTS_DATE_FORMAT, true)
  return (parsed.isValid() ? parsed : moment()).format('YYYY-MM-DD')
}

const HUDDLE_EVENTS_COLUMN_DEFS: {
  id: string
  leftLabel: string
  key: keyof Pick<
    HuddleEventsResult,
    'birthdays' | 'anniversaries' | 'achievements' | 'greetingReminders'
  >
}[] = [
  { id: 'events-birthdays', leftLabel: 'Birthdays', key: 'birthdays' },
  {
    id: 'events-anniversaries',
    leftLabel: 'Anniversaries',
    key: 'anniversaries',
  },
  { id: 'events-achievements', leftLabel: 'Achievements', key: 'achievements' },
  {
    id: 'events-greeting',
    leftLabel: 'Greeting reminders',
    key: 'greetingReminders',
  },
]

export function mapHuddleEventsToColumns(
  data: HuddleEventsResult | null | undefined,
): HuddleEventsColumn[] {
  return HUDDLE_EVENTS_COLUMN_DEFS.map((def) => ({
    id: def.id,
    leftLabel: def.leftLabel,
    people: data?.[def.key] ?? [],
  }))
}

export type HuddleScheduleEventTableRow = {
  id: string
  time: string
  event: string
  patient: string
  patientId: string | null
  impact: string
}

export function mapHuddleScheduleAdjustmentsSummaryToMetrics(
  summary: HuddleYesterdayScheduleAdjustmentsSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  const goalPct = summary.chairUtilizationGoalPct
  return [
    {
      id: 'y-no-shows',
      leftLabel: 'No-shows',
      value: String(summary.noShows),
    },
    {
      id: 'y-cancellations',
      leftLabel: 'Cancellations',
      value: String(summary.cancellations),
    },
    {
      id: 'y-broken-appts',
      leftLabel: 'Broken appointments',
      value: String(summary.brokenAppointments),
    },
    {
      id: 'y-chair-util',
      leftLabel: 'Chair utilization',
      value: formatPercent(summary.chairUtilizationPct),
      rightLabel:
        goalPct != null ? `Goal ${formatPercent(goalPct)}` : undefined,
    },
    {
      id: 'y-adjustments',
      leftLabel: 'Adjustments',
      value: formatCurrency(summary.adjustments, { compact: true }),
    },
  ]
}

export function mapHuddleScheduleEventsToTableRows(
  events: HuddleScheduleEventRow[] | null | undefined,
): HuddleScheduleEventTableRow[] {
  if (!events?.length) return []
  return events.map((row) => ({
    id: row.id,
    time: row.time,
    event: row.event,
    patient: row.patientName,
    patientId: row.patientId,
    impact: row.impact,
  }))
}

const formatOpenChairMinutes = (totalMinutes: number): string => {
  if (!Number.isFinite(totalMinutes) || totalMinutes <= 0) return '0 mins'
  if (totalMinutes < 60) return `${Math.round(totalMinutes)} mins`
  const hours = totalMinutes / 60
  if (hours < 10) {
    const rounded = Math.round(hours * 10) / 10
    return `${rounded} ${rounded === 1 ? 'hr' : 'hrs'}`
  }
  return `${Math.round(hours).toLocaleString('en-US')} hrs`
}

export function mapHuddleTomorrowScheduleSummaryToMetrics(
  summary: HuddleTomorrowScheduleAtAGlanceSummary | null | undefined,
): DashboardMetricItemData[] {
  if (!summary) return []

  return [
    {
      id: 'tm-patients',
      leftLabel: 'Patients scheduled',
      value: String(summary.patientsScheduled),
    },
    {
      id: 'tm-appointments',
      leftLabel: 'Appointments',
      value: String(summary.appointments),
    },
    {
      id: 'tm-providers',
      leftLabel: 'Providers in',
      value: String(summary.providersIn),
    },
    {
      id: 'tm-open-chair',
      leftLabel: 'Open chair time',
      value: formatOpenChairMinutes(summary.openChairMinutes),
    },
  ]
}

export type HuddleTomorrowScheduleTableRow = {
  id: string
  time: string
  patientName: string
  provider: string
  appointmentType: string
  operatory: string
  duration: string
  status: string
}

export function mapHuddleTomorrowScheduleAppointmentsToTableRows(
  appointments: HuddleTomorrowScheduleAtAGlanceRow[] | null | undefined,
): HuddleTomorrowScheduleTableRow[] {
  if (!appointments?.length) return []

  return appointments.map((row) => ({
    id: row.appointmentId,
    time: row.time,
    patientName: row.patientName,
    provider: row.provider,
    appointmentType: row.appointmentType,
    operatory: row.operatory,
    duration: row.duration,
    status: row.status,
  }))
}

export function huddleTodayForecastHasData(
  data: HuddleTodayForecastResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.expectedProduction.current,
      data.summary.scheduledProduction.current,
      data.summary.pendingTreatments.current,
      data.summary.arDue.current,
      data.summary.arOver90,
    ) || data.appointments.length > 0
  )
}

export function huddleTomorrowForecastHasData(
  data: HuddleTomorrowForecastResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.expectedProduction.current,
      data.summary.scheduledProduction.current,
      data.summary.pendingTreatments.current,
      data.summary.arDue.current,
      data.summary.arOver90,
    ) || data.appointments.length > 0
  )
}

export function huddleProviderPerformanceHasData(
  data: HuddleProviderPerformanceResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.sameDayTreatment.current,
      data.summary.acceptedTreatment.current,
      data.summary.hourlyProduction.current,
      data.summary.perioRate.current,
      data.summary.hygieneUtilization.current,
      data.summary.collections.current,
    ) ||
    data.providers.some((row) =>
      hasNonZeroDashboardValues(
        row.sameDayTreatmentPct,
        row.acceptedTreatmentMinutes,
        row.hourlyProduction,
        row.perioRate,
        row.collections,
      ),
    )
  )
}

export function huddleYesterdaysPerformanceHasData(
  data: HuddleYesterdaysPerformanceResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.production.current,
      data.summary.collections.current,
      data.summary.scheduledAppointments.current,
    ) ||
    data.providers.some((row) =>
      hasNonZeroDashboardValues(
        row.production,
        row.collections,
        row.scheduledAppointments,
      ),
    )
  )
}

export function huddleYesterdayActualsHasData(
  data: HuddleYesterdayActualsResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.production.current,
      data.summary.collections.current,
      data.summary.patientsSeen.current,
      data.summary.newPatients.current,
    ) ||
    data.summaryBreakdown.some((row) => hasNonZeroDashboardValues(row.amount))
  )
}

export function huddleScheduleAdjustmentsHasData(
  data: HuddleYesterdayScheduleAdjustmentsResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.noShows,
      data.summary.cancellations,
      data.summary.brokenAppointments,
      data.summary.chairUtilizationPct,
      data.summary.adjustments,
    ) || data.events.length > 0
  )
}

export function huddlePerformanceVsGoalHasData(
  data: HuddleYesterdayPerformanceResult | null | undefined,
): boolean {
  if (!data) return false
  // Targets/goals do not establish activity — only actuals do.
  return (
    hasNonZeroDashboardValues(
      data.summary.production.actual,
      data.summary.collections.actual,
    ) || data.measures.some((row) => hasNonZeroDashboardValues(row.actual))
  )
}

export function huddleScheduleAtAGlanceHasData(
  data: HuddleTomorrowScheduleAtAGlanceResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.patientsScheduled,
      data.summary.appointments,
    ) || data.appointments.length > 0
  )
}

export function huddleTomorrowGoalsHasData(
  data: HuddleTomorrowGoalsResult | null | undefined,
): boolean {
  if (!data) return false
  // Targets/goals do not establish activity — only projected actuals do.
  return hasNonZeroDashboardValues(
    data.goals.production.actual,
    data.goals.collections.actual,
    data.goals.patients.actual,
  )
}

export function huddleLeaderboardHasData(
  data: HuddleLeaderboardResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.totalProduction.current,
      data.summary.practiceAverage.current,
    ) ||
    data.providers.some((row) =>
      hasNonZeroDashboardValues(
        row.production,
        row.collections,
        row.appointmentsCompleted,
      ),
    )
  )
}

export function huddleYesterdayProviderActualsHasData(
  data: HuddleYesterdayProviderActualsResult | null | undefined,
): boolean {
  if (!data) return false
  return (
    hasNonZeroDashboardValues(
      data.summary.totalProduction.current,
      data.summary.totalCollection.current,
      data.summary.patientsSeen.current,
    ) ||
    data.providerBreakdown.some((row) =>
      hasNonZeroDashboardValues(
        row.production,
        row.collection,
        row.patientsSeen,
      ),
    )
  )
}

export function huddlePatientMetricsHaveData(
  metrics: DashboardMetricItemData[] | null | undefined,
): boolean {
  return hasNonZeroDashboardValues(
    ...(metrics?.map((metric) => metric.value) ?? []),
  )
}
